import type { AdminStore } from "types/adminStore";
import type { StoreApprovalItem, StoreApprovalStatus } from "./types";

const readString = (...values: unknown[]) => {
  const value = values.find(
    (candidate): candidate is string =>
      typeof candidate === "string" && candidate.trim().length > 0
  );

  return value?.trim() || "";
};

export const getStoreName = (store: AdminStore) =>
  readString(store.name, store.store_name, `Store #${store.id}`);

export const getOwnerName = (store: AdminStore) =>
  readString(store.owner_name, store.owner?.name, store.user?.name, "Unknown Owner");

export const getStoreLocation = (store: AdminStore) =>
  readString(store.address, store.location, "—");

export const getStoreImage = (store: AdminStore) =>
  readString(store.logo, store.image);

export const getStoreActiveStatus = (store: AdminStore) => {
  if (typeof store.is_active === "boolean") {
    return store.is_active ? "active" : "inactive";
  }

  return readString(store.status, "active").toLowerCase();
};

export const getStoreApprovalStatus = (
  store: AdminStore
): StoreApprovalStatus => {
  const approvalStatus =
    typeof store.approval_status === "string"
      ? store.approval_status.toLowerCase()
      : "";
  const status =
    typeof store.status === "string" ? store.status.toLowerCase() : "";

  return approvalStatus === "approved" || status === "approved"
    ? "approved"
    : "awaiting_approval";
};

export const isApprovedStore = (store?: AdminStore | null) =>
  Boolean(store && getStoreApprovalStatus(store) === "approved");

export const mapAdminStoreToApprovalItem = (
  store: AdminStore,
  marketName?: string,
  marketLocation?: string
): StoreApprovalItem => ({
  id: String(store.id),
  storeName: getStoreName(store),
  location: getStoreLocation(store),
  ownerName: getOwnerName(store),
  logoUrl: getStoreImage(store) || undefined,
  status: getStoreApprovalStatus(store),
  activeStatus: getStoreActiveStatus(store),
  submittedAt: store.created_at,
  ownerEmail: readString(store.owner?.email, store.user?.email) || undefined,
  ownerPhone: readString(store.owner?.phone, store.user?.phone) || undefined,
  bvnNumber: readString(store.bvnNumber, store.bvn_number) || undefined,
  market: readString(store.market?.name, store.market_name, marketName) || undefined,
  marketLocation:
    readString(store.market?.location, store.market_location, marketLocation) ||
    undefined,
  bvnNinFrontUrl:
    readString(store.bvnNinFrontUrl, store.bvn_nin_front_url) || undefined,
  bvnNinBackUrl:
    readString(store.bvnNinBackUrl, store.bvn_nin_back_url) || undefined,
});
